import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { Fragment, useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import { SafeFormattedMessage } from "./SafeFormattedMessage";
import { TextField, TextValidationField } from "../fields/TextField";
import {
  signupWatcher,
} from "../../redux/actions/loginactioncreators";
import { NewFADVPasswordField } from "../fields/NewFADVPasswordField";
import { LegacyLoginHelpLink } from "./LegacyLoginHelpLink";
import { navigateToLogin } from "../../redux/helpers/navigators";

export const SignUpMigrationForm = () => {
  const dispatch = useDispatch();
  const useAccountNumber = useSelector(state => state.loginForm.useAccountNumber);
  const legacyForgotPassword = useSelector(state => state.loginForm.legacyForgotPassword);
  const translatableErrorMessages = useSelector(state => state.loginForm.translatableMessages ?? []);
  const loginErrorMessages = useSelector(state => state.loginForm.loginErrorMessages);
  const successMessages = useSelector(state => state.loginForm.successMessages);

  const [errors, setErrors] = useState({});
  const [externalErrors, setExternalErrors] = useState({});
  const [formData, setFormData] = useState({});
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [forceValidateAll, setForceValidateAll] = useState(false);

  let fieldRefs = new Map();

  const requiredFields = useAccountNumber
    ? ["username", "password", "accountNumber"]
    : ["username", "password"];

  const requiredFieldsPresent = requiredFields.every(field => {
    const value = formData[field];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });

  useEffect(() => {
    successMessages?.forEach((loginMessage) => {
      enqueueSnackbar(loginMessage, { variant: "success" });
    });
  }, [successMessages]);

  useEffect(() => {
    const nextExternalErrors = {};

    loginErrorMessages?.forEach(({ key, message }) => {
      nextExternalErrors[key] = message;
    });

    translatableErrorMessages
      .filter(item => Boolean(item.message))
      .forEach(({ key, message, context = {}, options = {} }) => {
        if (options.variant === "error") {
          const errorMessage = message ? <SafeFormattedMessage {...message} values={context} /> : null;
          nextExternalErrors[key] = errorMessage;
        }
      });

    setExternalErrors(nextExternalErrors);
  }, [loginErrorMessages, translatableErrorMessages]);

  const reportError = (fieldName, errorMessage) => {
    setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
  };

  const clearError = (fieldName) => {
    setErrors(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  const handleInputChange = (e) => {
    if (forceValidateAll) {
      setForceValidateAll(false);
    }
    setFormData(prev => Object.assign({}, prev, { [e.target.id]: e.target.value }));
  };

  const onSubmitHandler = (e) => {
    e.preventDefault();
    setHasBeenSubmitted(true);
    setForceValidateAll(true);
    if (!requiredFieldsPresent) {
      for (const field of requiredFields) {
        const value = formData[field];
        if (value === undefined || value === null || String(value).trim() === "") {
          fieldRefs.get(field)?.focus();
          break;
        }
      }
      return;
    }
    if (Object.keys(errors).length > 0) {
      return;
    }
    const trimmedFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );
    const { username, password, accountNumber } = trimmedFormData;
    dispatch(signupWatcher({ username, password, accountNumber, newEmailRequired: false }));
    setExternalErrors({});
  };

  const showErrors = hasBeenSubmitted;
  const buttonDisabled = !requiredFieldsPresent || (Object.keys(errors).length > 0 && showErrors);
  const handleReturnToSignIn = () => {
      navigateToLogin();
    };

  const RenderErrors = ({ show }) => {
    const errorMessages = Object.values(errors).filter(Boolean).flat();
    const externalErrorMessages = Object.values(externalErrors).filter(Boolean).flat();
    const display = show && (errorMessages.length > 0 || externalErrorMessages.length > 0);
    return (
      <div
        id="verifyPasswordErrors"
        className={twMerge("bg-error-100 w-full border border-error-100 text-error-700 text-base pr-5 py-4 rounded mb-4", !display && "hidden")}
        role="alert"
        aria-live={display ? "assertive" : "off"}
      >
        <ul className="list-none pl-5 flex flex-col space-y-1">
          {externalErrorMessages.map((errMsg, index) => (
            <li key={index}>{errMsg}</li>
          ))}
          {Boolean(errorMessages.length && externalErrorMessages.length) && (
            <li role="separator" aria-hidden="true" className="list-none flex justify-center">
              <hr className="w-[75%] self-center text-error-200" />
            </li>
          )}
          {errorMessages.map((errMsg, index) => (
            <li key={index}>{errMsg}</li>
          ))}
        </ul>
      </div>
    );
  };
  RenderErrors.propTypes = {
    show: PropTypes.bool,
  };

  return (
    <Fragment>
      <h2 className="text-2xl text-primary-fg mb-6">
        <FormattedMessage id="signup.header" defaultMessage="Sign Up" description="Header text for sign-up migration entry form" />
      </h2>
      <div className="relative flex z-10">
        <RenderErrors show={showErrors} />
      </div>
      <form onSubmit={onSubmitHandler} className="space-y-5">
        <div className="space-y-4">
          <p className="text-primary-fg text-base leading-6">
            <FormattedMessage
              id="signup.intro"
              defaultMessage="To setup your login, enter your User ID, Client ID, and password. After setup, subsequent logins will require your Email address, Client ID, and password."
              description="Intro text for sign-up migration form"
            />
          </p>
          <TextField
            show={useAccountNumber}
            label={<FormattedMessage id="login.clientAccountId" defaultMessage="Client ID" description="Login form user input" />}
            id="accountNumber"
            reportError={reportError}
            clearError={clearError}
            onChange={handleInputChange}
            forceValidateAll={forceValidateAll}
            inputRef={node => { fieldRefs.set("accountNumber", node); }}
          />
          <TextValidationField
            label={<FormattedMessage id="login.userId" defaultMessage="User ID" description="Login form user ID input" />}
            name="username"
            id="username"
            reportError={reportError}
            clearError={clearError}
            onChange={handleInputChange}
            forceValidateAll={forceValidateAll}
            inputRef={node => { fieldRefs.set("username", node); }}
          />
          <NewFADVPasswordField
            label={<FormattedMessage id="login.password" defaultMessage="Password" description="Login form user input" />}
            id="password"
            type="password"
            reportError={reportError}
            clearError={clearError}
            onChange={handleInputChange}
            forceValidateAll={forceValidateAll}
            inputRef={node => { fieldRefs.set("password", node); }}
          />
          {legacyForgotPassword && (
            <div className="flex justify-end">
              <a
                id="forgotYourPassword"
                className="cursor-pointer text-Button-text text-sm font-medium underline"
                href={legacyForgotPassword}
              >
                <FormattedMessage id="login.forgotPasswordLinkText" defaultMessage="Forgot Password?" description="Text for the forgot password link in the login form" />
              </a>
            </div>
          )}
        </div>
        <button
          id="submit"
          type="submit"
          aria-disabled={buttonDisabled}
          data-disabled={buttonDisabled}
          disabled={buttonDisabled}
          className={twMerge("w-full bg-accent-600 hover:bg-blue-700 text-white font-medium py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg", buttonDisabled && "hover:bg-neutral-500 bg-neutral-400 cursor-not-allowed")}
        >
          <FormattedMessage id="signup.continueWithLogin" defaultMessage="Sign Up" description="Button text to continue sign-up flow with login credentials" />
        </button>
        <div className="flex flex-col justify-center items-center">
          <button
            type="button"
            id="returnToSignIn"
            className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors duration-200"
            onClick={handleReturnToSignIn}
          >
            <FormattedMessage id="signup.returnToSignIn" defaultMessage="Return to Sign In" description="Button text to return user from sign-up flow to login form" />
          </button>
        </div>
        <LegacyLoginHelpLink />
      </form>
    </Fragment>
  );
};
